import type { SessionKind } from '@ukinfo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import { Card, CardBody, Checkbox, SectionTitle, useToast } from './ui';

type Einstellung = Record<SessionKind, boolean>;

const ARTEN: { kind: SessionKind; label: string }[] = [
  { kind: 'MATCH', label: 'Spiele' },
  { kind: 'TRAINING', label: 'Trainings' },
  { kind: 'WORK', label: 'Arbeitseinsätze' },
];

/**
 * Push, wenn jemand in einer meiner Mannschaften zu- oder absagt.
 *
 * Je Terminart einschaltbar und anfangs aus: wer für viele Mannschaften
 * Rechte hat, soll nicht ungefragt bei jeder Antwort ein Signal bekommen.
 */
export function ResponsePushCard(): JSX.Element {
  const toast = useToast();
  const qc = useQueryClient();
  const einstellung = useQuery({
    queryKey: ['response-push'],
    queryFn: () => api.get<Einstellung>('/api/sports/response-push'),
  });
  const speichern = useMutation({
    mutationFn: (input: Partial<Einstellung>) => api.put<Einstellung>('/api/sports/response-push', input),
    onSuccess: (result, input) => {
      qc.setQueryData(['response-push'], result);
      const [kind, an] = Object.entries(input)[0] ?? [];
      const art = ARTEN.find((eintrag) => eintrag.kind === kind)?.label ?? '';
      toast.show(`Push bei Rückmeldungen zu ${art} ${an ? 'eingeschaltet' : 'ausgeschaltet'}`);
    },
    onError: (error: unknown) =>
      toast.show(error instanceof ApiError ? error.message : 'Einstellung konnte nicht gespeichert werden', 'error'),
  });

  return (
    <Card>
      <CardBody className="space-y-3">
        <SectionTitle
          title="Zu- und Absagen in meinen Mannschaften"
          subtitle="Push aufs Handy, sobald jemand seine Rückmeldung ändert"
        />
        <div className="flex flex-wrap gap-x-6 gap-y-2 rounded-lg bg-slate-50 px-3 py-2">
          {ARTEN.map(({ kind, label }) => (
            <Checkbox
              key={kind}
              label={label}
              checked={einstellung.data?.[kind] ?? false}
              disabled={!einstellung.data || speichern.isPending}
              onChange={(wert) => speichern.mutate({ [kind]: wert })}
            />
          ))}
        </div>
        <p className="text-xs text-slate-500">
          Gilt für alle Mannschaften, deren Rückmeldungen du sehen darfst. Eigene Änderungen melden sich nicht.
          Voraussetzung: Push ist oben auf diesem Gerät eingeschaltet.
        </p>
      </CardBody>
    </Card>
  );
}
